import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from './supabase'
import { getEffectivePermissions, hasPermission } from './lib/permissions'

const UserContext = createContext(null)

export function UserProvider({ children, session }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [org, setOrg] = useState(null)
  const [crmWorkspaces, setCrmWorkspaces] = useState([])
  const [activeCrmWorkspaceId, setActiveCrmWorkspaceIdState] = useState(null)
  const [businessUnits, setBusinessUnits] = useState([])
  const [activeBusinessUnitId, setActiveBusinessUnitIdState] = useState(null)
  const [loadingProfile, setLoadingProfile] = useState(true)

  useEffect(() => {
    fetchProfile({ authUser: session?.user, showLoading: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  async function fetchProfile({ authUser, showLoading = false } = {}) {
    if (showLoading) setLoadingProfile(true)

    if (!authUser) {
      setUser(null)
      setProfile(null)
      setOrg(null)
      setCrmWorkspaces([])
      setActiveCrmWorkspaceIdState(null)
      setBusinessUnits([])
      setActiveBusinessUnitIdState(null)
      setLoadingProfile(false)
      return
    }

    setUser(authUser)

    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*, permission_sets(id, name, permissions)')
      .eq('id', authUser.id)
      .single()

    if (profileError) {
      console.error('Failed to load profile:', profileError)
      setProfile(null)
      setOrg(null)
      setLoadingProfile(false)
      return
    }

    setProfile(profileData)

    const profileIsAdmin = profileData?.role === 'admin' || profileData?.is_platform_admin
    const [{ data: unitData, error: unitError }, { data: membershipData, error: membershipError }] = await Promise.all([
      supabase.from('business_units').select('id, org_id, name, is_default').eq('org_id', profileData.org_id).eq('archived', false).order('name'),
      supabase.from('business_unit_memberships').select('business_unit_id').eq('org_id', profileData.org_id).eq('user_id', authUser.id)
    ])

    if (unitError || membershipError) {
      console.error('Failed to load Business units:', unitError || membershipError)
      setBusinessUnits([])
      setActiveBusinessUnitIdState(null)
    } else {
      const assignedIds = new Set((membershipData || []).map(item => item.business_unit_id))
      const availableUnits = profileIsAdmin ? (unitData || []) : (unitData || []).filter(unit => assignedIds.has(unit.id))
      const storageKey = `delivr.business-unit.${authUser.id}`
      const storedUnitId = window.localStorage.getItem(storageKey)
      const nextUnitId = availableUnits.some(unit => unit.id === storedUnitId)
        ? storedUnitId
        : profileIsAdmin ? null : availableUnits[0]?.id || null

      setBusinessUnits(availableUnits)
      setActiveBusinessUnitIdState(nextUnitId)
      if (nextUnitId) window.localStorage.setItem(storageKey, nextUnitId)
    }

    let loadedOrg = null

    if (profileData?.org_id) {
      const { data: orgData, error: orgError } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', profileData.org_id)
        .maybeSingle()

      if (orgError) {
        console.error('Failed to load organisation:', orgError)
        setOrg(null)
      } else {
        loadedOrg = orgData || null
        setOrg(loadedOrg)
      }
    } else {
      setOrg(null)
    }

    if (loadedOrg?.crm_enabled) {
      const { data: workspaceData, error: workspaceError } = await supabase
        .from('crm_workspaces')
        .select('id, org_id, name, base_currency, archived, crm_workspace_members(role, visibility_mode, user_id)')
        .eq('org_id', profileData.org_id)
        .eq('archived', false)
        .eq('crm_workspace_members.user_id', authUser.id)
        .order('name')

      if (workspaceError) {
        console.error('Failed to load CRM workspaces:', workspaceError)
        setCrmWorkspaces([])
        setActiveCrmWorkspaceIdState(null)
      } else {
        const availableWorkspaces = workspaceData || []
        const storageKey = `delivr.crm-workspace.${authUser.id}`
        const storedWorkspaceId = window.localStorage.getItem(storageKey)
        const nextWorkspaceId = availableWorkspaces.some(workspace => workspace.id === storedWorkspaceId)
          ? storedWorkspaceId
          : availableWorkspaces[0]?.id || null

        setCrmWorkspaces(availableWorkspaces)
        setActiveCrmWorkspaceIdState(nextWorkspaceId)
        if (nextWorkspaceId) window.localStorage.setItem(storageKey, nextWorkspaceId)
      }
    } else {
      setCrmWorkspaces([])
      setActiveCrmWorkspaceIdState(null)
    }

    setLoadingProfile(false)
  }

  const isAdmin = profile?.role === 'admin'
  const isPlatformAdmin = !!profile?.is_platform_admin
  const orgId = profile?.org_id || null
  const planType = profile?.plan_type || 'rights_holder'
  const crmEnabled = !!org?.crm_enabled || planType === 'agency'
  const isAgency = crmEnabled
  const activeCrmWorkspace = crmWorkspaces.find(workspace => workspace.id === activeCrmWorkspaceId) || null
  const activeCrmMembership = activeCrmWorkspace?.crm_workspace_members?.[0] || null
  const permissions = getEffectivePermissions(profile)
  const can = permission => hasPermission(profile, permission)

  function setActiveCrmWorkspaceId(workspaceId) {
    if (!crmWorkspaces.some(workspace => workspace.id === workspaceId)) return
    setActiveCrmWorkspaceIdState(workspaceId)
    if (user?.id) window.localStorage.setItem(`delivr.crm-workspace.${user.id}`, workspaceId)
  }

  function setActiveBusinessUnitId(businessUnitId) {
    if (!businessUnitId && (isAdmin || isPlatformAdmin)) {
      setActiveBusinessUnitIdState(null)
      if (user?.id) window.localStorage.removeItem(`delivr.business-unit.${user.id}`)
      return
    }
    if (!businessUnits.some(unit => unit.id === businessUnitId)) return
    setActiveBusinessUnitIdState(businessUnitId)
    if (user?.id) window.localStorage.setItem(`delivr.business-unit.${user.id}`, businessUnitId)
  }

  return (
    <UserContext.Provider value={{
      user,
      profile,
      org,
      orgId,
      isAdmin,
      isPlatformAdmin,
      planType,
      isAgency,
      crmEnabled,
      businessUnits,
      activeBusinessUnitId,
      setActiveBusinessUnitId,
      crmWorkspaces,
      activeCrmWorkspace,
      activeCrmWorkspaceId,
      activeCrmMembership,
      setActiveCrmWorkspaceId,
      permissions,
      can,
      loadingProfile,
      refreshProfile: fetchProfile
    }}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  return useContext(UserContext)
}
